import { create } from 'zustand';
import type { ApiKey, N8nInstance } from '@n8n-builder/api-client';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';
import { CACHE_TTL, fetchWithCache } from '../utils/cache';
import { useSettingsStore } from './settings';

interface MCPState {
  // State
  apiKeys: ApiKey[];
  instances: N8nInstance[];
  isLoading: boolean;
  error: string | null;
  instancesLastFetchedAt: number | null;

  // Actions
  setApiKeys: (apiKeys: ApiKey[]) => void;
  setInstances: (instances: N8nInstance[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // API Key helpers
  addApiKey: (apiKey: ApiKey) => void;
  removeApiKey: (id: string) => void;

  // Instance helpers
  addInstance: (instance: N8nInstance) => void;
  updateInstance: (id: string, updates: Partial<N8nInstance>) => void;
  removeInstance: (id: string) => void;
  setInstanceActive: (id: string) => void;

  // Cache actions
  fetchInstances: (options?: { force?: boolean }) => void;
  invalidateInstances: () => void;
}

export const useMCPStore = create<MCPState>((set, get) => ({
  // Initial state
  apiKeys: [],
  instances: [],
  isLoading: false,
  error: null,
  instancesLastFetchedAt: null,

  // Setters
  setApiKeys: (apiKeys) => set({ apiKeys }),
  setInstances: (instances) => set({ instances, instancesLastFetchedAt: Date.now() }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  // API Key helpers
  addApiKey: (apiKey) =>
    set((state) => ({
      apiKeys: [apiKey, ...state.apiKeys],
    })),

  removeApiKey: (id) =>
    set((state) => ({
      apiKeys: state.apiKeys.filter((k) => k.id !== id),
    })),

  // Instance helpers
  addInstance: (instance) =>
    set((state) => ({
      instances: [instance, ...state.instances],
      instancesLastFetchedAt: null,
    })),

  updateInstance: (id, updates) =>
    set((state) => ({
      instances: state.instances.map((inst) =>
        inst.id === id ? { ...inst, ...updates } : inst
      ),
      instancesLastFetchedAt: null,
    })),

  removeInstance: (id) => {
    const wasActive = get().instances.find((inst) => inst.id === id)?.isActive;
    set((state) => ({
      instances: state.instances.filter((inst) => inst.id !== id),
      instancesLastFetchedAt: null,
    }));
    if (wasActive) {
      useSettingsStore.getState().invalidateActiveInstance();
    }
  },

  setInstanceActive: (id) => {
    set((state) => ({
      instances: state.instances.map((inst) =>
        inst.id === id ? { ...inst, isActive: true } : inst
      ),
      instancesLastFetchedAt: null,
    }));
    // Cross-store: invalidate settings active instance cache
    useSettingsStore.getState().invalidateActiveInstance();
  },

  // Cache actions
  fetchInstances: (options) => {
    const state = get();
    fetchWithCache(
      state.instances.length > 0,
      state.instancesLastFetchedAt,
      CACHE_TTL.instances,
      async () => {
        if (state.instances.length === 0) {
          set({ isLoading: true });
        }
        try {
          const apiClient = createApiClient(API_BASE_URL);
          const result = await apiClient.getInstances();
          if (result.data?.instances) {
            set({ instances: result.data.instances, instancesLastFetchedAt: Date.now() });
          }
        } catch (error) {
          console.error('Failed to fetch instances:', error);
        } finally {
          set({ isLoading: false });
        }
      },
      options,
    );
  },

  invalidateInstances: () => set({ instancesLastFetchedAt: null }),
}));
